// "ขอโอนเครื่องเป็นทรัพย์สิน": the FlowMemo goods-to-asset form, shown inside the step and
// prefilled from the SO. The user checks it, picks whose eMemo login signs it, and VERP sends it.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../config/api';
import EmemoPicker from './EmemoPicker';

const num = (v) => Number(v) || 0;
const money = (n) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('th-TH', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const Field = ({ label, children, wide }) => (
  <div className="rs-field" style={wide ? { gridColumn: '1 / -1' } : undefined}>
    <label>{label}</label>
    {children}
  </div>
);

const AssetTransferForm = ({ orderId, onSubmitted }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [accountName, setAccountName] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_URL}/rental/${orderId}/asset-form`);
      setData(resp.data);
      setForm(resp.data.form);
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดแบบฟอร์มไม่สำเร็จ');
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  if (error && !data) return <div className="econ-alert" role="alert"><div className="econ-alert__line">{error}</div></div>;
  if (!data || !form) return <p className="rs-help">กำลังเตรียมแบบฟอร์ม...</p>;

  if (data.submission) {
    const s = data.submission;
    return (
      <div className="wlog__pill wlog__pill--ok" role="status">
        <i className="bi bi-check-circle-fill"></i> ส่งแบบฟอร์มแล้ว {fmt(s.at)}{s.by ? ` · ในนาม ${s.by}` : ''}{s.reference ? ` · เลขที่เอกสาร ${s.reference}` : ''}
      </div>
    );
  }

  const set = (name) => (e) => setForm(f => ({ ...f, [name]: e.target.value }));
  const setItem = (i, name, value) => setForm(f => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, [name]: value } : it)) }));
  const subtotal = form.items.reduce((sum, it) => sum + num(it.qty) * num(it.unit_price), 0);

  const chooseShipping = (e) => {
    const a = data.shipping.find(x => x.id === e.target.value);
    setForm(f => ({
      ...f, ship_addr_id: e.target.value,
      ...(a ? {
        address_line1: a.address_line1 || '', address_line2: a.address_line2 || '', city: a.city || '', zip_code: a.zip_code || '',
        formatted_shipping_address: [a.address_line1, a.address_line2, a.city, a.zip_code].filter(Boolean).join(' ')
      } : {})
    }));
  };

  const send = async () => {
    setError('');
    if (!window.confirm(`ส่งแบบฟอร์มขอโอนเครื่องเป็นทรัพย์สินในนาม ${accountName || 'ผู้ส่งที่เลือก'}?\nเมื่อส่งแล้วจะเริ่มขั้นตอนอนุมัติทันที และส่งซ้ำไม่ได้`)) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/rental/${orderId}/asset-form/submit`, { form, account_id: accountId });
      toast.success('ส่งแบบฟอร์มเรียบร้อยแล้ว');
      await load();
      if (onSubmitted) onSubmitted();
    } catch (err) {
      setError(err.response?.data?.message || 'ส่งแบบฟอร์มไม่สำเร็จ');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="atf">
      <h3 className="atf__h">ข้อมูลทั่วไป</h3>
      <div className="atf__grid">
        <Field label="ผู้ขออนุมัติ"><input className="rs-input" readOnly value={`${form.full_name || '—'} (${form.emp_code})`} /></Field>
        <Field label="แผนก / ฝ่าย"><input className="rs-input" readOnly value={form.department_name} /></Field>
        <Field label="ลูกค้า"><input className="rs-input" readOnly value={`${form.customer_name} (${form.cus_code})`} /></Field>
        <Field label="เบอร์โทรลูกค้า"><input className="rs-input" value={form.phone_number} onChange={set('phone_number')} /></Field>
        <Field label="ที่อยู่จัดส่ง" wide>
          <select className="rs-input" value={form.ship_addr_id} onChange={chooseShipping}>
            {data.shipping.length === 0 && <option value="">— ไม่พบที่อยู่ในระบบ —</option>}
            {data.shipping.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
        </Field>
        <Field label="สโตร์ *">
          <input className="rs-input" list="atf-locations" value={form.location} onChange={set('location')} placeholder="พิมพ์ค้นหาสโตร์" />
          <datalist id="atf-locations">{data.locations.map(l => <option key={l} value={l} />)}</datalist>
        </Field>
      </div>

      <h3 className="atf__h">รายการ</h3>
      <div className="rs-table-wrap">
        <table className="rs-table">
          <thead><tr><th>รหัสสินค้า</th><th>ชื่อ</th><th className="is-num">จำนวน</th><th className="is-num">ราคา/หน่วย (ราคากลาง)</th><th className="is-num">รวม</th></tr></thead>
          <tbody>
            {form.items.map((it, i) => (
              <tr key={i}>
                <td className="rs-mono">{it.item_code}</td>
                <td>{it.item_name}</td>
                <td className="is-num"><input className="rs-input atf__num" inputMode="numeric" value={it.qty} onChange={(e) => setItem(i, 'qty', e.target.value.replace(/[^\d]/g, ''))} /></td>
                <td className="is-num"><input className="rs-input atf__num" inputMode="decimal" value={it.unit_price} onChange={(e) => setItem(i, 'unit_price', e.target.value.replace(/[^\d.]/g, ''))} /></td>
                <td className="is-num">{money(num(it.qty) * num(it.unit_price))}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr><td colSpan={4} className="is-num">ยอดรวม (ก่อน VAT)</td><td className="is-num"><b>{money(subtotal)}</b></td></tr>
          </tfoot>
        </table>
      </div>
      {data.missing?.items?.length > 0 && (
        <p className="rs-help rs-help--warn">ไม่พบรหัส {data.missing.items.join(', ')} ในรายการสินค้าของ ERP กรุณาตรวจสอบรหัสและราคาก่อนส่ง</p>
      )}

      <h3 className="atf__h">หมายเหตุ</h3>
      <div className="atf__grid">
        <Field label="สาเหตุที่เบิก" wide><input className="rs-input" value={form.reason} onChange={set('reason')} /></Field>
        <Field label="ชื่อผู้ติดต่อ"><input className="rs-input" value={form.contact_name} onChange={set('contact_name')} /></Field>
        <Field label="เบอร์โทรผู้ติดต่อ"><input className="rs-input" value={form.contact_phone} onChange={set('contact_phone')} /></Field>
      </div>

      <h3 className="atf__h">ผู้ลงนามใน eMemo</h3>
      <EmemoPicker onChange={(id, name) => { setAccountId(id); setAccountName(name); }} />

      {error && <div className="econ-alert" role="alert"><div className="econ-alert__line">{error}</div></div>}
      <button type="button" className="rs-btn rs-btn--primary" disabled={sending || !accountId || !form.location.trim()} onClick={send}>
        {sending ? 'กำลังส่ง...' : 'ส่งแบบฟอร์มขอโอนเป็นทรัพย์สิน'}
      </button>
      {(!accountId || !form.location.trim()) && (
        <span className="rs-help" style={{ marginLeft: 12 }}>
          {!form.location.trim() ? 'เลือกสโตร์' : 'เลือกผู้ลงนามใน eMemo'} ก่อนส่ง
        </span>
      )}
    </div>
  );
};

export default AssetTransferForm;
